import type { Camera } from './geometry';

/**
 * The home hero's loop, as data (handoff-hero README, section 4).
 *
 * One timeline drives everything: the state at any moment is the initial state
 * with every step at or before that moment applied in order. Nothing is chained
 * off another timer, so nothing drifts, and the whole loop is testable without
 * a browser.
 *
 * The story: an organiser (est.28) searches for their event and opens it,
 * shows the public page, and an athlete enters a Pairs team on their phone.
 * The phone follows the real athlete path, condensed: the event page, the
 * Select Ticket sheet "Get Tickets" opens, team payment with Apple Pay, then
 * the confirmation page, scrolled to the Instagram story card and the wallet,
 * the branded Apple Wallet pass, and the branded confirmation email arriving.
 * Then the organiser's bell rings and the numbers go up.
 *
 * The handoff's phone section was 5 seconds and three invented screens. This
 * one is longer on purpose: the payment, the story card, the pass and the
 * email are the moments an organiser recognises, and they need time to read.
 */

export type HeroView = 'home' | 'event';

/** Where the cursor is pointing. Resolved to a position by measuring the DOM. */
export type CursorTarget =
  | 'rest' | 'searchResult' | 'viewPublic'
  | 'getTickets' | 'pairs' | 'continue' | 'applePay'
  | 'addWallet' | 'passAdd' | 'notification';

/** Which Safari page the phone is showing. */
export type PhoneScreen = 'event' | 'sheet' | 'pay' | 'done';

/** How far down the confirmation page the athlete has scrolled. */
export type DoneScroll = 'top' | 'story' | 'wallet';

/** The Apple Pay system sheet. */
export type ApplePaySheet = 'off' | 'confirm' | 'done';

/** Which phone control is mid-press, for the tap feedback. */
export type PhonePress = 'getTickets' | 'continue' | 'applePay' | 'addWallet' | 'passAdd' | 'notification' | null;

export interface HeroState {
  /* ── Dashboard: search ── */
  focus: boolean;
  text: string;
  results: boolean;
  resultHot: boolean;
  resultPressed: boolean;
  view: HeroView;
  track: boolean;
  viewPublicPressed: boolean;

  /** Phone layout only: which side of the dashboard the camera shows. */
  camera: Camera;

  /* ── Cursor ── */
  cursor: CursorTarget;
  cursorOn: boolean;
  ripple: boolean;

  /* ── Phone ── */
  phone: boolean;
  screen: PhoneScreen;
  pairsSelected: boolean;
  press: PhonePress;
  applePay: ApplePaySheet;
  doneScroll: DoneScroll;
  /** The Apple Wallet "add pass" sheet. */
  pass: boolean;
  /** The confirmation email's notification banner. */
  notification: boolean;
  /** Mail open on the confirmation email, and whether it has scrolled. */
  email: boolean;
  emailScrolled: boolean;

  /* ── The entry landing ── */
  bell: number;
  badge: boolean;
  note: boolean;
  row: boolean;
  entries: number;

  /** Frame content visibility; off briefly so the reset happens unseen. */
  body: boolean;
}

export const INITIAL_STATE: HeroState = {
  focus: false,
  text: '',
  results: false,
  resultHot: false,
  resultPressed: false,
  view: 'home',
  track: false,
  viewPublicPressed: false,
  camera: 'left',
  cursor: 'rest',
  cursorOn: false,
  ripple: false,
  phone: false,
  screen: 'event',
  pairsSelected: false,
  press: null,
  applePay: 'off',
  doneScroll: 'top',
  pass: false,
  notification: false,
  email: false,
  emailScrolled: false,
  bell: 0,
  badge: false,
  note: false,
  row: false,
  entries: 64,
  body: true,
};

/** What the organiser types, at a readable typing pace. */
export const SEARCH_TERM = 'Hybrid';
export const TYPE_START_MS = 1300;
export const TYPE_INTERVAL_MS = 160;

const typing: Array<[number, Partial<HeroState>]> = SEARCH_TERM.split('').map((_, i) => [
  TYPE_START_MS + i * TYPE_INTERVAL_MS,
  { text: SEARCH_TERM.slice(0, i + 1) },
]);


/** Every cursor move gets this long before its tap: the travel is 0.7s. */
export const CURSOR_TRAVEL_MS = 750;

export const STEPS: ReadonlyArray<readonly [number, Partial<HeroState>]> = [
  [1100, { focus: true }],
  ...typing,
  [2150, { results: true }],
  // The cursor comes in, hovers the event, and clicks it, so the organiser
  // is seen choosing it rather than the page just changing under them.
  [2250, { cursorOn: true, cursor: 'searchResult' }],
  [3000, { resultHot: true }],
  [3450, { resultPressed: true, ripple: true }],
  [3600, { ripple: false }],
  [3700, { results: false, focus: false, text: '', resultHot: false, resultPressed: false, view: 'event' }],
  [4000, { track: true }],
  // On a phone the camera pans right with the cursor, and stays there: the
  // bell, the notification and the new activity row all land on that side.
  [4200, { cursor: 'viewPublic', camera: 'right' }],
  [4950, { viewPublicPressed: true, ripple: true }],
  [5130, { viewPublicPressed: false, ripple: false }],

  // The athlete's phone, on the organiser's public page.
  [5200, { phone: true, screen: 'event' }],
  [5450, { cursor: 'getTickets' }],
  [6200, { ripple: true, press: 'getTickets' }],
  // "Get Tickets" opens the Select Ticket sheet in place; it does not navigate.
  [6350, { ripple: false, press: null, screen: 'sheet' }],
  [6850, { cursor: 'pairs' }],
  [7600, { ripple: true, pairsSelected: true }],
  [7750, { ripple: false }],
  [7850, { cursor: 'continue' }],
  [8600, { ripple: true, press: 'continue' }],
  [8750, { ripple: false, press: null, screen: 'pay' }],

  // Payment: a beat to read it, then Apple Pay.
  [9800, { cursor: 'applePay' }],
  [10550, { ripple: true, press: 'applePay' }],
  [10700, { ripple: false, press: null, applePay: 'confirm', cursorOn: false }],
  [11800, { applePay: 'done' }],
  [12400, { applePay: 'off', screen: 'done' }],

  // The confirmation: down to the story card, then the wallet.
  [13600, { doneScroll: 'story' }],
  [15200, { doneScroll: 'wallet' }],
  [15600, { cursorOn: true, cursor: 'addWallet' }],
  [16350, { ripple: true, press: 'addWallet' }],
  [16500, { ripple: false, press: null, pass: true }],
  [17700, { cursor: 'passAdd' }],
  [18450, { ripple: true, press: 'passAdd' }],
  [18600, { ripple: false, press: null, pass: false }],

  // The branded confirmation email lands, and opens.
  [19100, { notification: true }],
  [19600, { cursor: 'notification' }],
  [20350, { ripple: true, press: 'notification' }],
  [20500, { ripple: false, press: null, notification: false, email: true, cursorOn: false }],
  [21100, { emailScrolled: true }],
  [23500, { phone: false }],

  // The organiser's side: the bell rings and the pair lands.
  [24000, { bell: -16, badge: true }],
  [24090, { bell: 13 }],
  [24180, { bell: -9 }],
  [24270, { bell: 6 }],
  [24360, { bell: 0, note: true, row: true, entries: 65 }],
  // Two places, one pair.
  [24510, { entries: 66 }],
  [26900, { note: false }],

  // Fade, reset unseen, fade back in.
  [28500, { body: false }],
  [29000, { ...INITIAL_STATE, body: false }],
  [29250, { body: true }],
];

export const LOOP_MS = 29700;

/** How many steps have fired by `elapsed` ms into the loop. */
export function stepIndexAt(elapsed: number): number {
  let n = 0;
  while (n < STEPS.length && STEPS[n][0] <= elapsed) n++;
  return n;
}

/** The state after the first `count` steps. */
export function stateAfter(count: number): HeroState {
  let state = INITIAL_STATE;
  for (let i = 0; i < count && i < STEPS.length; i++) state = { ...state, ...STEPS[i][1] };
  return state;
}

/** The state `elapsed` ms into the loop. Wraps, so any clock value is valid. */
export function stateAt(elapsed: number): HeroState {
  const t = ((elapsed % LOOP_MS) + LOOP_MS) % LOOP_MS;
  return stateAfter(stepIndexAt(t));
}

/* ── Figures ──
   Everything that ticks derives from `entries`, never animated separately, so
   the numbers can never disagree with each other mid-loop. */

export const CAP = 100;
/** Pairs is £150 a team; each place in it is £75 to the organiser. */
export const PRICE_PER_PLACE = 75;
const BASE_ENTRIES = 64;
const BASE_TAKEN = 4960;
const DIVISION_BASE = 50;
const DIVISION_CAP = 20;

export function figures(entries: number) {
  const taken = BASE_TAKEN + (entries - BASE_ENTRIES) * PRICE_PER_PLACE;
  return {
    entries,
    taken,
    takenLabel: `£${taken.toLocaleString('en-GB')}`,
    soldPct: Math.round((entries / CAP) * 100),
    division: entries - DIVISION_BASE,
    divisionPct: Math.round(((entries - DIVISION_BASE) / DIVISION_CAP) * 100),
  };
}
